import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { FXAAShader } from "three/examples/jsm/shaders/FXAAShader.js";

export function supportsWebGL() {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(window.WebGLRenderingContext && (canvas.getContext("webgl2") || canvas.getContext("webgl")));
  } catch (error) {
    return false;
  }
}

function optionValue(options, key, fallback) {
  return options[key] === undefined ? fallback : options[key];
}

function createSceneBackdrop(options) {
  const canvas = document.createElement("canvas");
  canvas.width = 4;
  canvas.height = 512;
  const context = canvas.getContext("2d");
  const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
  const top = new THREE.Color(optionValue(options, "backgroundTop", 0x061630));
  const horizon = new THREE.Color(optionValue(options, "backgroundHorizon", optionValue(options, "background", 0x0a3155)));
  const bottom = new THREE.Color(optionValue(options, "backgroundBottom", 0x08223e));
  gradient.addColorStop(0, top.getStyle());
  gradient.addColorStop(0.56, horizon.getStyle());
  gradient.addColorStop(1, bottom.getStyle());
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  return texture;
}

export function createEnergyRuntime(container, options = {}) {
  const width = Math.max(container.clientWidth, 1);
  const height = Math.max(container.clientHeight, 1);
  const scene = new THREE.Scene();
  const backdrop = createSceneBackdrop(options);
  scene.background = backdrop;
  scene.fog = new THREE.FogExp2(optionValue(options, "fog", 0x092744), optionValue(options, "fogDensity", 0.008));

  const camera = new THREE.PerspectiveCamera(options.fov || 34, width / height, 0.1, 400);
  camera.position.fromArray(options.camera || [34, 29, 42]);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.35));
  renderer.setSize(width, height);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = optionValue(options, "exposure", 1.18);
  container.appendChild(renderer.domElement);

  let gpuName = "WebGL";
  try {
    const gl = renderer.getContext();
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    gpuName = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
  } catch (error) {
    gpuName = "WebGL";
  }
  const softwareRenderer = /swiftshader|llvmpipe|software/i.test(gpuName || "");
  const usePostprocessing = options.postprocessing !== false && !softwareRenderer;
  let postprocessingActive = usePostprocessing;
  let lowFpsSamples = 0;
  renderer.domElement.dataset.engine = `three.js r${THREE.REVISION}`;
  renderer.domElement.dataset.gpu = gpuName;
  renderer.domElement.dataset.postFx = usePostprocessing ? "selective" : "adaptive-off";

  const composer = new EffectComposer(renderer);
  composer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1));
  composer.setSize(width, height);
  composer.addPass(new RenderPass(scene, camera));
  const outlinePass = new OutlinePass(new THREE.Vector2(width, height), scene, camera);
  outlinePass.edgeStrength = 2.4;
  outlinePass.edgeGlow = 0.55;
  outlinePass.edgeThickness = 1.15;
  outlinePass.visibleEdgeColor.set(options.outlineColor || 0x48e8dc);
  outlinePass.hiddenEdgeColor.set(0x174b68);
  composer.addPass(outlinePass);
  const bloomPass = new UnrealBloomPass(new THREE.Vector2(width, height), options.bloomStrength || 0.22, 0.22, 0.9);
  composer.addPass(bloomPass);
  const fxaaPass = new ShaderPass(FXAAShader);
  fxaaPass.material.uniforms.resolution.value.set(1 / (width * renderer.getPixelRatio()), 1 / (height * renderer.getPixelRatio()));
  composer.addPass(fxaaPass);
  composer.addPass(new OutputPass());

  const ambient = new THREE.HemisphereLight(
    optionValue(options, "hemisphereSky", 0x91cfff),
    optionValue(options, "hemisphereGround", 0x18344c),
    optionValue(options, "ambient", 1.45),
  );
  scene.add(ambient);
  const key = new THREE.DirectionalLight(optionValue(options, "keyColor", 0xc5eaff), optionValue(options, "keyIntensity", 2.65));
  key.position.set(18, 36, 24);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.camera.left = -60;
  key.shadow.camera.right = 60;
  key.shadow.camera.top = 60;
  key.shadow.camera.bottom = -60;
  scene.add(key);
  const fill = new THREE.DirectionalLight(optionValue(options, "fillColor", 0x5f9fd3), optionValue(options, "fillIntensity", 0.9));
  fill.position.set(-28, 22, -24);
  scene.add(fill);
  const softFill = new THREE.AmbientLight(optionValue(options, "softFillColor", 0x315b7a), optionValue(options, "softFillIntensity", 0.22));
  scene.add(softFill);
  const rim = new THREE.PointLight(optionValue(options, "rimColor", 0x1fded5), optionValue(options, "rimIntensity", 65), 90, 2);
  rim.position.set(-16, 10, -10);
  scene.add(rim);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.07;
  controls.target.fromArray(options.target || [0, 2, 0]);
  controls.enablePan = false;
  controls.minDistance = options.minDistance || 34;
  controls.maxDistance = options.maxDistance || 72;
  controls.minPolarAngle = Math.PI * 0.23;
  controls.maxPolarAngle = Math.PI * 0.42;
  controls.minAzimuthAngle = options.minAzimuth || -Math.PI * 0.28;
  controls.maxAzimuthAngle = options.maxAzimuth || Math.PI * 0.28;

  const grid = new THREE.GridHelper(options.gridSize || 90, options.gridDivisions || 36, options.gridColor || 0x176ea6, 0x0a355f);
  grid.material.transparent = true;
  grid.material.opacity = optionValue(options, "gridOpacity", 0.3);
  scene.add(grid);

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  let frameId = 0;
  let lastTime = performance.now();
  let metricStartedAt = performance.now();
  let metricFrames = 0;
  let metricRenderMs = 0;
  const tickHandlers = [];
  const clock = new THREE.Clock();
  let documentVisible = !document.hidden;
  let containerVisible = true;
  const visibilityHandler = () => { documentVisible = !document.hidden; };
  document.addEventListener("visibilitychange", visibilityHandler);
  const observer = typeof IntersectionObserver !== "undefined" ? new IntersectionObserver((entries) => {
    containerVisible = entries[0] ? entries[0].isIntersecting : true;
  }, { threshold: 0.01 }) : null;
  if (observer) observer.observe(container);

  function render(time) {
    const delta = Math.min(clock.getDelta(), 0.05);
    if (documentVisible && containerVisible) {
      const renderStartedAt = performance.now();
      tickHandlers.forEach((handler) => handler(delta, time || performance.now()));
      controls.update();
      if (postprocessingActive) composer.render();
      else renderer.render(scene, camera);
      metricRenderMs += performance.now() - renderStartedAt;
      metricFrames += 1;
      const metricNow = time || performance.now();
      if (metricNow - metricStartedAt >= 1000) {
        let sceneDrawCalls = 0;
        let sceneTriangles = 0;
        scene.traverse((object) => {
          if (!object.visible || !(object.isMesh || object.isLine || object.isLineSegments || object.isPoints)) return;
          sceneDrawCalls += 1;
          if (object.geometry) {
            const positionCount = object.geometry.index ? object.geometry.index.count : (object.geometry.attributes.position ? object.geometry.attributes.position.count : 0);
            sceneTriangles += Math.round(positionCount / 3) * (object.isInstancedMesh ? object.count : 1);
          }
        });
        const sampledFps = Math.round(metricFrames * 1000 / (metricNow - metricStartedAt));
        const averageRenderMs = metricRenderMs / Math.max(metricFrames, 1);
        renderer.domElement.dataset.fps = String(sampledFps);
        renderer.domElement.dataset.renderMs = averageRenderMs.toFixed(1);
        renderer.domElement.dataset.frameCapacity = String(Math.min(60, Math.round(1000 / Math.max(averageRenderMs, 0.1))));
        renderer.domElement.dataset.drawCalls = String(sceneDrawCalls);
        renderer.domElement.dataset.triangles = String(sceneTriangles);
        if (postprocessingActive && averageRenderMs > 24) {
          lowFpsSamples += 1;
          if (lowFpsSamples >= 2) {
            postprocessingActive = false;
            renderer.domElement.dataset.postFx = "adaptive-off";
          }
        } else {
          lowFpsSamples = 0;
        }
        metricFrames = 0;
        metricRenderMs = 0;
        metricStartedAt = metricNow;
      }
    }
    lastTime = time || lastTime;
    frameId = requestAnimationFrame(render);
  }
  frameId = requestAnimationFrame(render);

  function resize() {
    const nextWidth = Math.max(container.clientWidth, 1);
    const nextHeight = Math.max(container.clientHeight, 1);
    camera.aspect = nextWidth / nextHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(nextWidth, nextHeight);
    composer.setSize(nextWidth, nextHeight);
    outlinePass.setSize(nextWidth, nextHeight);
    fxaaPass.material.uniforms.resolution.value.set(1 / (nextWidth * renderer.getPixelRatio()), 1 / (nextHeight * renderer.getPixelRatio()));
  }

  function pick(event, objects) {
    const rect = renderer.domElement.getBoundingClientRect();
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);
    const hits = raycaster.intersectObjects(objects, true);
    if (!hits.length) return null;
    let object = hits[0].object;
    while (object && !object.userData.assetId) object = object.parent;
    return object || null;
  }

  function dispose() {
    cancelAnimationFrame(frameId);
    document.removeEventListener("visibilitychange", visibilityHandler);
    if (observer) observer.disconnect();
    controls.dispose();
    scene.traverse((object) => {
      if (object.geometry) object.geometry.dispose();
      const materials = object.material ? (Array.isArray(object.material) ? object.material : [object.material]) : [];
      materials.forEach((material) => {
        Object.keys(material).forEach((keyName) => {
          const value = material[keyName];
          if (value && value.isTexture) value.dispose();
        });
        material.dispose();
      });
    });
    backdrop.dispose();
    renderer.dispose();
    composer.passes.forEach((pass) => { if (pass.dispose) pass.dispose(); });
    if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
  }

  function setOutline(objects) {
    outlinePass.selectedObjects = objects || [];
  }

  return { THREE, scene, camera, renderer, composer, controls, pick, resize, dispose, setOutline, tickHandlers, lastTime, usePostprocessing };
}

export function attachAsset(object, asset) {
  object.userData.assetId = asset.id;
  object.userData.asset = asset;
  object.traverse((child) => {
    child.userData.assetId = asset.id;
    child.userData.asset = asset;
  });
  return object;
}

export function setAssetHighlight(objects, assetId) {
  objects.forEach((object) => {
    const active = object.userData.assetId === assetId;
    object.traverse((child) => {
      if (!child.material || !child.material.emissive) return;
      if (child.userData.baseEmissive === undefined) child.userData.baseEmissive = child.material.emissive.getHex();
      child.material.emissive.setHex(active ? 0x146d7e : child.userData.baseEmissive);
      child.material.emissiveIntensity = active ? 1.25 : 0.32;
    });
  });
}

export function createFlowParticles(THREERef, points, color = 0x45f4dc, count = 14) {
  const curve = new THREERef.CatmullRomCurve3(points.map((point) => new THREERef.Vector3(...point)));
  const tube = new THREERef.Mesh(
    new THREERef.TubeGeometry(curve, 80, 0.055, 6, false),
    new THREERef.MeshBasicMaterial({ color, transparent: true, opacity: 0.27 })
  );
  const geometry = new THREERef.SphereGeometry(0.12, 10, 10);
  const material = new THREERef.MeshBasicMaterial({ color });
  const particles = Array.from({ length: count }, (_, index) => {
    const particle = new THREERef.Mesh(geometry, material);
    particle.userData.offset = index / count;
    return particle;
  });
  return {
    curve,
    tube,
    particles,
    update(speed, time) {
      particles.forEach((particle) => {
        const progress = (particle.userData.offset + time * speed) % 1;
        particle.position.copy(curve.getPointAt(progress));
      });
    },
  };
}
